import { hexToRGB, rgColor } from './colorUtils';
import { getCurrentXFDFDateString, parseXFDFDate } from './dateUtils';

export class XFDFAnnot {
    private annot: Element;

    constructor(xmlElement: Element) {
        this.annot = xmlElement;
    }

    get type(): string {
        return this.annot.tagName;
    }
    get page(): number {
        return Number(this.annot.getAttribute('page') ?? '0');
    }
    get author(): string {
        return this.annot.getAttribute('title') ?? '';
    }

    setPage(pageIndex: number) {
        this.annot.setAttribute('page', pageIndex.toString());
    }
    /** Array format: [llx, lly, urx, ury] (lower-left x, lower-left y, upper-right x, upper-right y) */
    getRect(): number[] {
        return (this.annot.getAttribute('rect')?.split(',') ?? ['0', '500', '200', '1000']).map(x =>
            Number(x),
        );
    }
    /** Array format: [llx, lly, urx, ury] (lower-left x, lower-left y, upper-right x, upper-right y) */
    setRect(rect: number[]) {
        this.annot.setAttribute('rect', rect.join(','));
    }
    getColor(): string {
        return this.annot.getAttribute('color') ?? '#000000';
    }
    setColor(color: string) {
        this.annot.setAttribute('color', color);
    }
    getFillColor(): string {
        return this.annot.getAttribute('interior-color') ?? 'transparent';
    }
    setFillColor(color: string) {
        if (['square', 'circle', 'polygon'].includes(this.type)) {
            this.annot.setAttribute('interior-color', color);
        }
    }
    getTextColor(): string {
        return this.annot.getAttribute('TextColor') ?? '#000000';
    }
    setTextColor(color: string) {
        if (this.annot.hasAttribute('TextColor')) {
            this.annot.setAttribute('TextColor', color);
        }
    }
    getModifiedDate(): Date | null {
        const date = this.annot.getAttribute('date');
        return date ? parseXFDFDate(date) : null;
    }
    setModifiedDate() {
        this.annot.setAttribute('date', getCurrentXFDFDateString());
    }
    getCreationDate(): Date | null {
        const date = this.annot.getAttribute('creationdate');
        return date ? parseXFDFDate(date) : null;
    }
    setCreationDate() {
        this.annot.setAttribute('creationdate', getCurrentXFDFDateString());
    }
    setAuthor(author: string) {
        this.annot.setAttribute('title', author);
    }
    getId(): string {
        return this.annot.getAttribute('name') ?? '';
    }
    setId(guid: string) {
        this.annot.setAttribute('name', guid);
    }
    removeColor() {
        this.annot.removeAttribute('color');
    }
    /** Freetext bordercolor */
    setDefaultAppearanceColor(color: string) {
        const defaultappearance = Array.from(this.annot.children).find(
            el => el.tagName === 'defaultappearance',
        );

        if (!defaultappearance) return;

        // <defaultappearance>0.5 1 0 rg /Cousine 20 Tf</defaultappearance>
        const font = defaultappearance.innerHTML.split('rg')[1].trim();

        const { r, g, b } = hexToRGB(color);
        defaultappearance.innerHTML = `${rgColor(r)} ${rgColor(g)} ${rgColor(b)} rg ${font}`;
    }
    setDefaultStyleColor(color: string) {
        const defaultstyle = Array.from(this.annot.children).find(
            el => el.tagName === 'defaultstyle',
        );

        if (!defaultstyle) return;

        const colorAttr = defaultstyle.innerHTML
            .split(';')
            .find(attr => attr.trim().startsWith('color:'));

        if (colorAttr) {
            defaultstyle.innerHTML = defaultstyle.innerHTML.replace(colorAttr, `color: ${color}`);
        }
    }
    /** Callout textcolor */
    setStyleColors(color: string) {
        const contentsrichtext = Array.from(this.annot.children).find(
            el => el.tagName === 'contents-richtext',
        );

        if (!contentsrichtext) return;

        Array.from(contentsrichtext.getElementsByTagName('*')).forEach(el => {
            const styleAttr = el.getAttribute('style');

            if (!styleAttr) return;

            const colorAttr = styleAttr.split(';').find(attr => attr.startsWith('color:'));

            if (colorAttr) {
                el.setAttribute('style', styleAttr.replace(colorAttr, `color:${color}`));
            }
        });
    }
    /** Delete from XFDF document */
    delete() {
        this.annot.remove();
    }
    /** Returns wether the annotation was created or modified during this session. */
    isFromSession(sessionStarttime: number): boolean {
        const creationDate = this.getCreationDate();
        if (creationDate && creationDate.getTime() > sessionStarttime) {
            return true;
        }

        const modifiedDate = this.getModifiedDate();
        if (modifiedDate && modifiedDate.getTime() > sessionStarttime) {
            return true;
        }

        return false;
    }
}

export function parseXFDFDoc(xfdf: string): { xfdfDoc: Document; xfdfAnnots: XFDFAnnot[] } {
    const parser = new DOMParser();
    const xfdfDoc = parser.parseFromString(xfdf, 'text/xml');

    const annotsTag = xfdfDoc.getElementsByTagName('annots')?.[0];
    const xfdfAnnots = Array.from(annotsTag?.children ?? []).map(el => new XFDFAnnot(el));

    return { xfdfDoc, xfdfAnnots };
}

export function serializeXFDFDoc(xfdfDoc: Document): string {
    return new XMLSerializer().serializeToString(xfdfDoc);
}
